/**
 * <s-gallery-thumbs target="gallery-id">
 * Thumbnail list for a horizontal snap gallery. Clicking a [data-thumb-index]
 * button scrolls the gallery to that slide; scrolling the gallery marks the
 * matching thumbnail with aria-current="true".
 *
 * Desktop (styles in config/styles.css, .gallery-nav*): adds arrows at the ends of the thumbnail
 * strip when it has more thumbnails than fit, so
 * mouse users can reach every photo. Mobile keeps swiping.
 */
class SGalleryThumbs extends HTMLElement {
  #ac = null;

  connectedCallback() {
    const gallery = document.getElementById(this.getAttribute('target'));
    if (!gallery) return;
    const buttons = [...this.querySelectorAll('[data-thumb-index]')];
    if (!buttons.length) return;
    const strip = this.querySelector('ul');

    this.#ac = new AbortController();
    const { signal } = this.#ac;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const behavior = () => (reduceMotion.matches ? 'auto' : 'smooth');
    let current = 0;

    const setCurrent = (index) => {
      current = index;
      buttons.forEach((button, i) => {
        if (i === index) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
      // Keep the active thumbnail in view inside the strip without scrolling the page.
      const thumb = buttons[index]?.closest('li') || buttons[index];
      if (strip && thumb) {
        const left = thumb.offsetLeft - strip.offsetLeft;
        if (left < strip.scrollLeft || left + thumb.offsetWidth > strip.scrollLeft + strip.clientWidth) {
          strip.scrollTo({ left: left - (strip.clientWidth - thumb.offsetWidth) / 2, behavior: behavior() });
        }
      }
      updateNav();
    };

    const goTo = (index) => {
      const slide = gallery.children[index];
      if (!slide) return;
      // offsetLeft is relative to the gallery and already accounts for RTL layout.
      gallery.scrollTo({ left: slide.offsetLeft - gallery.offsetLeft, behavior: behavior() });
      setCurrent(index);
    };

    const makeArrow = (dir, cls, label) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = `gallery-nav ${cls} gallery-nav--${dir}`;
      b.setAttribute('aria-label', label);
      b.innerHTML = `<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${dir === 'prev' ? 'M12.5 4.5 7 10l5.5 5.5' : 'M7.5 4.5 13 10l-5.5 5.5'}"/></svg>`;
      return b;
    };


    // Thumbnail strip arrows, shown only when the strip overflows.
    let stripPrev = null;
    let stripNext = null;
    if (strip) {
      this.classList.add('gallery-thumbs-nav');
      stripPrev = makeArrow('prev', 'gallery-nav--strip', 'Scroll thumbnails back');
      stripNext = makeArrow('next', 'gallery-nav--strip', 'Scroll thumbnails forward');
      this.append(stripPrev, stripNext);
      const page = (d) => strip.scrollBy({ left: d * strip.clientWidth * 0.8, behavior: behavior() });
      stripPrev.addEventListener('click', () => page(-1), { signal });
      stripNext.addEventListener('click', () => page(1), { signal });
      strip.addEventListener('scroll', () => updateNav(), { passive: true, signal });
      window.addEventListener('resize', () => updateNav(), { signal });
    }

    function updateNav() {


      if (strip && stripPrev && stripNext) {
        const max = strip.scrollWidth - strip.clientWidth;
        const pos = Math.abs(strip.scrollLeft);
        stripPrev.hidden = max <= 2 || pos <= 2;
        stripNext.hidden = max <= 2 || pos >= max - 2;
      }
    }

    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-thumb-index]');
      if (!button) return;
      goTo(Number(button.dataset.thumbIndex));
    }, { signal });

    let frame = 0;
    gallery.addEventListener('scroll', () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const width = gallery.clientWidth || 1;
        const index = Math.round(Math.abs(gallery.scrollLeft) / width);
        if (index !== current) setCurrent(index);
      });
    }, { passive: true, signal });

    updateNav();
    requestAnimationFrame(updateNav);
    this.#followVariant(goTo, () => current, signal);
  }

  /**
   * Show each variant's own image when the variant changes (data-variant-media from the
   * gallery-variant-media snippet). Live Product Options swatches (radios named "v_<Option>") switch it
   * straight away; the theme's picker is followed through the form's selected variant id.
   */
  #followVariant(goTo, getCurrent, signal) {
    let entries = [];
    try { entries = JSON.parse(this.dataset.variantMedia || '[]'); } catch (e) { return; }
    if (!entries.length) return;

    let lastId = this.dataset.currentVariant || '';
    const show = (entry) => {
      if (!entry) return;
      lastId = entry.id;
      if (entry.media !== getCurrent()) goTo(entry.media);
    };
    const byId = (id) => entries.find((entry) => entry.id === String(id));

    // Arriving on a specific variant (?variant=): open on its image.
    const urlVariant = new URL(window.location.href).searchParams.get('variant');
    if (urlVariant) show(byId(urlVariant));

    const fromSwatches = () => {
      const picked = {};
      document.querySelectorAll('input[name^="v_"]:checked').forEach((input) => {
        picked[input.name.slice(2).trim()] = input.value.trim();
      });
      const names = Object.keys(picked);
      if (!names.length) return null;
      return entries.find((entry) => names.every((name) => !(name in entry.options) || entry.options[name] === picked[name])
        && names.some((name) => name in entry.options)) || null;
    };

    const fromForm = () => {
      const input = document.querySelector('[data-product-form] input[name="id"]');
      if (!input || !input.value || input.value === lastId) return null;
      lastId = input.value;
      return byId(input.value);
    };

    document.addEventListener('change', (event) => {
      const target = event.target;
      if (target && target.name && target.name.indexOf('v_') === 0) {
        show(fromSwatches());
      } else {
        requestAnimationFrame(() => show(fromForm()));
      }
    }, { signal });

    // The product form is redrawn after a variant change; pick up the new variant id.
    const form = document.querySelector('[data-product-form]');
    if (form) {
      new MutationObserver(() => show(fromForm())).observe(form, { childList: true, subtree: true, attributes: true, attributeFilter: ['value'] });
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }
}

if (!customElements.get('s-gallery-thumbs')) {
  customElements.define('s-gallery-thumbs', SGalleryThumbs);
}
